import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { logImpression } from '@office-start/telemetry-service';
import { KnownImpressionInstrumentationEvents } from '@office-start/telemetry-types';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { OfficeFeedDataService } from '../officefeed/officefeed-data.service';
import { ActivityDescriptionService } from '../shared/activity/activity-description.service';
import { AnalyticsService } from '../shared/analytics/analytics.service';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { CreateMenuService } from '../shared/create-menu/create-menu.service';
import { ExpandableListLoadAnimation, ExpandableListModel } from '../shared/directives/expandable-list/expandable-list.model';
import { ListState } from '../shared/directives/expandable-list/list-state.enum';
import { TabItemState, TabListModel } from '../shared/directives/tab-list/tab-list.model';
import { InstrumentationEvent, InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { Cacheable } from '../shared/models/cacheable.model';
import { FileItem, FileType } from '../shared/models/file-item.model';
import { Form, GetFormsResponse } from '../shared/models/form.model';
import { DocumentImpressionEvent } from '../shared/models/impression-event.model';
import { ItemView } from '../shared/models/item-view.model';
import { KnownWorkloadReferralNames } from '../shared/models/known-workload-referral-names.model';
import { GraphGetOneNoteNotebooksResponse } from '../shared/models/onenote-graph.model';
import { ClassNoteBook } from '../shared/models/onenote.model';
import { OperationStream } from '../shared/models/operation-stream.model';
import { PaintMarkerName } from '../shared/models/paint-marker-name.enum';
import { SectionLoadState, SectionState } from '../shared/models/section-state.enum';
import { KnownTabIds, tabIdToSubArea, TabItem, TabItemAttribution } from '../shared/models/tab-item.model';
import { MruItemComponent, MruItemStates } from '../shared/mru-item/mru-item.component';
import { ActionType } from '../shared/mru/activity.model';
import { MruConfig, MruConfigService } from '../shared/mru/mru-config.service';
import { toFileItemFromClassNoteBook, toFileItemFromForm, toFileItemFromNoteBook, toFileItemFromOfficeFeed, toFileItemFromRecent, toFileItemFromShared, toMruDataItem } from '../shared/mru/mru-data-converter.utility';
import { MruData, MruDataService } from '../shared/mru/mru-data.service';
import { MruFileFormat, MruFileItem } from '../shared/mru/mru-file-item.model';
import { BrowserPaintMeasurementService } from '../shared/services/browser-paint-measurement.service';
import { DataSecurityService } from '../shared/services/data-security.service';
import { DeviceDetector } from '../shared/services/device-detector.service';
import { DocumentService } from '../shared/services/document.service';
import { EventService } from '../shared/services/event.service';
import { JumpListService } from '../shared/services/jump-list.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { OneDriveService } from '../shared/services/onedrive.service';
import { OneNoteConfigService } from '../shared/services/onenote-config.service';
import { GetNotebookFetchStrategy, OneNoteGraphService } from '../shared/services/onenote-graph.service';
import { OneNoteService } from '../shared/services/onenote.service';
import { PerformanceService } from '../shared/services/performance.service';
import { SwayConfigService } from '../shared/services/sway-config.service';
import { ThemingService } from '../shared/services/theming.service';
import { UrlAuthenticatorService } from '../shared/services/url-authenticator.service';
import { VisualService } from '../shared/services/visual.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { AppSpecificResource, LocService } from '../shared/shared-context/loc.service';
import { UserContextService } from '../shared/shared-context/user-context.service';
import { UrlBuilder } from '../shared/utilities/url-builder.utility';
import { UploadConfigService } from '../upload/upload-config.service';
import { OneDriveUploadService } from '../upload/upload.service';
import { TabListIdConstants } from './../shared/directives/tab-list/tab-list-ids.constants';
import { FormsService } from './../shared/services/forms.service';
import { DocumentCollection } from './models/document-collection.model';
import { ChangePinStateEvent, DeleteOptions, RemoveMruItemEvent } from './models/mru-state-event.model';
import { SharedServiceData, SharedServiceDataItem } from './shared-with-me/shared-with-me.model';
import { SharedWithMeService } from './shared-with-me/shared-with-me.service';

const enum MruPerformanceMarker {
    CachedMruReady = 'Cached_MRU_Ready', // Marker for denoting MRU data is loaded, and displayed, but from served from SW cache.
    MruReady = 'MRU_Ready' // Marker for denoting MRU data is loaded, displayed, and served from the actual MRU++ network response
}

/**
 * The main controller for the MRU module.
 */
@Component({
    selector: 'ohp-mru-component',
    templateUrl: './mru.component.html'
})
export class MruComponent implements OnInit, OnDestroy {
    @Input() public fileType: FileType;

    @Input() public maxInitialDocs: number;

    @Input() public maxTotalDocs: number;

    // Disables the header and new button
    @Input() public headless: boolean;

    @Input() public recentMruStreamData: OperationStream<MruData>;

    @Input() public readonly title: string;

    @Output() public readonly stateChanged: EventEmitter<SectionState>;

    @ViewChild('recentTab') public recentTabElement: ElementRef;

    public defaultView: ItemView;
    public createButtonEnabled: boolean;
    public uploadEnabled: boolean;
    public filterEnabled: boolean;
    public focusOnFilter: boolean;
    public recentSectionLandmarkAriaLabel: string;
    public showAsCard: boolean;
    public pinnedDocsEnabled: boolean;
    public lastOpened: string;
    public sharedWithMeViewEnabled: boolean;
    public tileViewEnabled: boolean;
    public trendingDocsEnabled: boolean;
    public recentTabListModel: TabListModel;
    public selectedTab: number;
    public initialListState: ListState;
    public focusFirstItem: boolean;
    public themed: boolean;
    public emptyRecentNewButtonEnabled: boolean;
    public showActivity: boolean;
    public showSharing: boolean;
    public showAnalytics: boolean;
    public numLoggableItems: number = 0;
    public showMobileShareAndActivity: boolean;
    public isConsumer: boolean;
    public maxInitialDocuments: number;
    public maxTotalDocuments: number;
    public screenReaderText: string;
    public fluentRefresh: boolean;
    public forceHideMru: boolean;
    public newMruEmptyStateEnabled: boolean;
    public isVisualRefresh = false;

    public readonly emptyViewName = 'empty-view';

    private componentArea: string;
    private mruConfig: MruConfig;
    private impressionId: string;
    private documents: DocumentCollection;
    private recentTab: TabItem;
    private pinnedTab: TabItem;
    private sharedWithMeTab: TabItem;
    private trendingTab: TabItem;
    private myAppItemsTab: TabItem;
    private initialTabsPromise: Promise<void>;
    private pendingTabImpressionEvent: DocumentImpressionEvent;
    private tabImpressionIndex = 0;
    private appFilter: Set<FileType>;
    private maxInitialDocumentCards: number;
    private appSuffix: string;
    private recentMruStream: Subscription;
    private listAnimation: ExpandableListLoadAnimation;
    private routeSubscription: Subscription;
    private mobile: boolean;
    private sharedWithMeSubscription: Subscription;
    private trendingDocSubscription: Subscription;
    private appItemStreamSubscription: Subscription;
    private themeShadesSubscription: Subscription;
    private darkMode: boolean;
    private sharedData: SharedServiceData | GetFormsResponse<Form>;
    private sharedWithMeTabListData: Array<MruFileItem>;
    private isFormsApp = false;
    private showInCardView: boolean;
    private visualRefreshSubscription: Subscription;

    private static readonly maxCardsPerLine = 4;

    // This is used for shell 'deep link' logic with ?section=recentdocs that autoscrolls the user to that section of the page.
    // It's static so that this logic doesn't run on subsequent navigations where we instantiate and render an MRU component (i.e. On WAC Start page navigations)
    private static shellDeeplinkPromise: Promise<void>;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly contextService: ContextService,
        private readonly dataSecurityService: DataSecurityService,
        private readonly deviceDetector: DeviceDetector,
        private readonly documentService: DocumentService,
        private readonly eventService: EventService,
        private readonly formsService: FormsService,
        private readonly instrumentationService: InstrumentationService,
        private readonly jumpListService: JumpListService,
        private readonly locService: LocService,
        private readonly mruConfigService: MruConfigService,
        private readonly officeFeedService: OfficeFeedDataService,
        private readonly onenoteGraphService: OneNoteGraphService,
        private readonly oneNoteService: OneNoteService,
        private readonly onNoteConfigService: OneNoteConfigService,
        private readonly performanceService: PerformanceService,
        private readonly mruDataService: MruDataService,
        private readonly sharedWithMeService: SharedWithMeService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly userContextService: UserContextService,
        private readonly uploadConfigService: UploadConfigService,
        private readonly activityDescriptionService: ActivityDescriptionService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly swayServiceConfigService: SwayConfigService,
        private readonly analyticsService: AnalyticsService,
        private readonly browserPaintMeasurementService: BrowserPaintMeasurementService,
        private readonly urlAuthenticatorService: UrlAuthenticatorService,
        private readonly oneDriveUploadService: OneDriveUploadService,
        private readonly createMenuService: CreateMenuService,
        private readonly oneDriveService: OneDriveService,
        private readonly themingService: ThemingService,
        private readonly visualService: VisualService
    ) {
        this.stateChanged = new EventEmitter<SectionState>();
        this.listAnimation = this.capabilitiesService.capabilities.disableAnimation ? ExpandableListLoadAnimation.None : ExpandableListLoadAnimation.FadeAndScale;
    }

    public ngOnInit() {
        MruItemComponent.isFirstClick = true;
        this.fluentRefresh = this.capabilitiesService.capabilities.fluentRefresh;
        this.isFormsApp = this.fileType === FileType.Forms;
        this.showInCardView = this.isFormsApp;
        this.filterEnabled = this.isFormsApp;
        this.onFilter = this.onFilter.bind(this);
        this.defaultView = this.isFormsApp ? ItemView.Card : ItemView.List;
        const capabilities = this.capabilitiesService.capabilities;
        this.stateChanged.emit({ state: SectionLoadState.Loading });
        this.componentArea = KnownAreas.Documents;
        this.recentSectionLandmarkAriaLabel = this.locService.RecentDocumentsLandmarkAriaLabel;
        this.themed = this.userContextService.user.themed;
        this.mruConfig = this.mruConfigService.config;
        this.pinnedDocsEnabled = this.mruConfig.mruPinEnabled;
        this.selectedTab = 0;
        this.tileViewEnabled = this.mruConfig.tileViewEnabled || this.isFormsApp; // Forms team wants to always show thumbnail view
        this.trendingDocsEnabled = this.mruConfig.trendingAroundMeEnabled && this.fileType !== FileType.Sway && !this.isFormsApp;
        this.mobile = !this.deviceDetector.isDesktopDevice();
        this.appFilter = this.fileType ? new Set([this.fileType]) : null;
        this.appSuffix = this.fileType ? `-${FileType[this.fileType].toLowerCase()}` : '';
        this.maxInitialDocumentCards = this.maxInitialDocs || (2 * MruComponent.maxCardsPerLine);
        this.maxInitialDocuments = this.maxInitialDocs || this.mruConfig.maxInitialListItems;
        this.maxTotalDocuments = this.maxTotalDocs || this.mruConfig.maxTotalListItems;
        this.showAnalytics = this.mruConfig.analyticsEnabled && this.fileType === FileType.Sway;
        this.showActivity = !this.showAnalytics && this.mruConfig.activityEnabled && this.fileType !== FileType.OneNote && !this.isFormsApp;
        this.showSharing = !this.showAnalytics && this.mruConfig.sharingStatusEnabled && !this.isFormsApp;
        this.showMobileShareAndActivity = this.mruConfig.mobileShareAndActivityEnabled;
        this.isConsumer = this.clientStartupConfigService.config.authType === AuthType.MSA;
        this.newMruEmptyStateEnabled = this.isConsumer && this.capabilitiesService.capabilities.newMruEmptyStateEnabled;
        this.sharedWithMeViewEnabled = this.mruConfig.sharedWithMeViewEnabled && this.fileType !== FileType.Sway && !(this.isConsumer && this.isFormsApp);

        if (this.capabilitiesService.capabilities.darkModeEnabled) {
            this.themeShadesSubscription = this.themingService.darkModeSubscription.subscribe(event => {
                this.darkMode = event;
                this.updateEmptyStates();
            });
        }

        if (this.capabilitiesService.capabilities.visualBackgroundEnabled) {
            this.visualRefreshSubscription = this.visualService.visualConfigSubscription.subscribe(visualEvent => {
                this.isVisualRefresh = visualEvent.visualBackgroundEnabled;
            });
        }

        if (capabilities.oneDriveUploadEnabled && this.uploadConfigService.config && this.fileType !== FileType.Sway && this.fileType !== FileType.OneNote && !this.isFormsApp) {
            this.oneDriveUploadService.uploadEnabledPromise.then(uploadEnabled => this.uploadEnabled = uploadEnabled);
        }

        this.createMenuService.createButtonEnabledPromise.then(createButtonEnabled => this.createButtonEnabled = createButtonEnabled);

        this.routeSubscription = this.officeRouteService.subscribeToRouteChangeEnd(event => {
            if (event.currentRoute) {
                const currentRoute = event.currentRoute.route;
                const previousRoute = event.previousRoute ? event.previousRoute.route : null;

                // Disable animations when navigating back to an MRU on either the homepage or start pages.
                if ((previousRoute === OfficeRoute.Templates && currentRoute === OfficeRoute.Start) ||
                    (previousRoute !== null && currentRoute === OfficeRoute.OfficeHome)) {
                    this.listAnimation = ExpandableListLoadAnimation.None;
                }

                // Update mru when navigating back from a wac document content page
                if (previousRoute === OfficeRoute.Content ||
                    this.isFormsApp && (previousRoute === OfficeRoute.MyForms || previousRoute === OfficeRoute.GroupForms)) {
                    this.mruDataService.updateMruDocuments(this.appFilter);
                }
            }
        });

        this.recentTabListModel = new TabListModel({
            id: this.isFormsApp ? TabListIdConstants.FormsTabList : TabListIdConstants.Recent,
            area: this.componentArea,
            tabs: [],
            logImpression: this.logTabImpression.bind(this),
            headerText: this.title,
            tabActivationAriaLabel: this.locService.MruTabActivationAriaLabel
        });

        // This enables using mru component independently from documents component (e.g. Edge Iframe)
        if (!this.recentMruStreamData) {
            this.recentMruStreamData = this.mruDataService.getMruDocuments(this.appFilter);
        }

        // Listen to updates from MRU Observable to update the view
        this.recentMruStream = this.recentMruStreamData.results.subscribe(data => this.processRecentMruTask(data));

        this.recentMruStreamData.initialCompletion.then(error => {
            if (!error) {
                return;
            }
            this.globalErrorHandler.handleCacheObservableError(error, ErrorId.jM9a5, ErrorId.al4U3);
            if (!this.recentTab) {
                this.stateChanged.emit({ state: SectionLoadState.Failed });
            }
        });

        // On first result from the Observable, we need to do some setup of the other tabs.
        const recentMruFirstEmit = this.recentMruStreamData.results.pipe(take(1)).toPromise().then(result => {
            if (!result) {
                return Promise.reject('Observable completed with no values');
            } else {
                return result.items.length;
            }
        });

        // Broadcast mruReady event (for PWA to hide extended splash screen), for ANTP needed for view cache, for MSA to load Dime and show no content actions
        if (capabilities.isPwaInUwp || this.isConsumer) {
            recentMruFirstEmit.then(result => this.eventService.broadcastEvent({
                name: AngularEvents.mruReady,
                args: [result]
            }));
        }

        this.initialTabsPromise = recentMruFirstEmit.then(() => {
            if (FileType.OneNote === this.fileType && this.onNoteConfigService.config.enableOneNoteApis) {
                this.recentTabListModel.updateOrAddTab(this.createMyAppItemsLoadingTab(this.fileType));
            }
            if (this.sharedWithMeViewEnabled) {
                this.createSharedLoadingTab();
                this.recentTabListModel.updateOrAddTab(this.sharedWithMeTab);
            }
            if (FileType.OneNote === this.fileType && this.userContextService.user.isEdu && this.onNoteConfigService.config.enableOneNoteApis) {
                this.recentTabListModel.updateOrAddTab(this.createClassNoteBookLoadingTab());
            }
            if (this.trendingDocsEnabled) {
                this.createTrendingLoadingTab();
                this.recentTabListModel.updateOrAddTab(this.trendingTab);
            }
        }).catch(() => { /* Do nothing */ });
    }

    public ngOnDestroy() {
        this.logPendingTabImpression();
        this.recentMruStream?.unsubscribe();
        this.routeSubscription?.unsubscribe();
        this.sharedWithMeSubscription?.unsubscribe();
        this.trendingDocSubscription?.unsubscribe();
        this.appItemStreamSubscription?.unsubscribe();
        this.themeShadesSubscription?.unsubscribe();
        this.visualRefreshSubscription?.unsubscribe();
    }

    public onTabClicked(index: number) {
        this.selectedTab = index;
        this.focusFirstItem = false;
        this.updateNumLoggableItems();
        this.updateWithNewZeroStateCheck();
    }

    public onDeleteItem(event: RemoveMruItemEvent) {
        if (event.deleteOption === DeleteOptions.UnpinnedShared) {
            if (event.item.fileType === FileType.Forms) {
                this.sendMruItemState(MruItemStates.Loading, event.item);
                this.formsService.unpinSharedForm(event.item.docId).then(() => {
                    const sharedForms: GetFormsResponse<Form> = this.sharedData as GetFormsResponse<Form>;
                    const removalIndex = sharedForms.value.findIndex(i => i.id === event.item.docId);
                    sharedForms.value.splice(removalIndex, 1);
                    sharedForms.fromCache = false;
                    this.processSharedDocTask(sharedForms, false);

                    this.sendMruItemState(MruItemStates.Resting, event.item);
                }).catch(() => {
                    this.sendMruItemState(MruItemStates.Resting, event.item);
                });
            }
        } else if (event.deleteOption === DeleteOptions.Move) {
            // No-op
        } else {
            // We will delete the item regardless of the result of the call to the Mru++ service.
            // Therefore, we are deleting the item from the list before actually calling the service.
            const item = event.item;
            this.documents.removeItem(item);
            this.mruDataService.removeDocument(toMruDataItem(item));
            this.updateListModels();

            this.setFocusOnStateChange(event.index);
        }
    }

    public onFilter(filterText: string) {
        this.updateListModels(filterText);
        this.focusOnFilter = true;

        if (filterText) {
            const resultSize = this.recentTabListModel.tabs[this.selectedTab].listModel.items.length;
            this.screenReaderText = this.locService.format(this.locService.FilterResultsFound, resultSize.toString());
        } else {
            this.screenReaderText = this.locService.NoFiltersApplied;
        }
    }

    public onChangePinState(event: ChangePinStateEvent) {
        const item = event.item;
        this.documents.removeItem(item);

        item.isPinned = event.isPinned;
        this.documents.addItem(item);

        this.updateListModels();

        this.mruDataService.updatePinnedState(toMruDataItem(item));
        this.changeDetector.detectChanges();
        this.setFocusOnStateChange(event.index, event.element);
    }

    public trackByIndex(index: number) {
        return index;
    }

    private updateEmptyStates() {
        if (!this.appSuffix && this.recentTabListModel && this.recentTabListModel.tabs && this.recentTabListModel.tabs.length) { // do nothing on start pages
            this.pinnedTab.zeroExperienceImage = this.darkMode ? 'emptystate-icons-sprite-darkmode emptystate-pinned-darkmode' : 'emptystate-icons-sprite emptystate-pinned';
            this.recentTabListModel.updateOrAddTab(this.pinnedTab);
            this.recentTab.zeroExperienceImage = this.darkMode ? 'emptystate-icons-sprite-darkmode emptystate-recent-darkmode' : 'emptystate-icons-sprite emptystate-recent';
            this.recentTabListModel.updateOrAddTab(this.recentTab);
            this.sharedWithMeTab.zeroExperienceImage = this.darkMode ? 'emptystate-icons-sprite-darkmode emptystate-swm-darkmode' : 'emptystate-icons-sprite emptystate-swm';
            this.recentTabListModel.updateOrAddTab(this.sharedWithMeTab);
            if (this.trendingTab) { // Consumer experience has no trending tab, so when this is called on theme change in dark mode, we run into an unhandled exception
                this.trendingTab.zeroExperienceImage = this.darkMode ? 'emptystate-icons-sprite-darkmode emptystate-discover-darkmode' : 'emptystate-icons-sprite emptystate-discover';
                this.recentTabListModel.updateOrAddTab(this.trendingTab);
            }
        }
    }

    private setFocusOnStateChange(index: number, eventElement?: ElementRef) {
        // Wait a tick for the UI to be updated
        setTimeout(() => {
            const selectedTab = this.recentTabListModel.tabs[this.selectedTab];
            let elementToFocus;
            if (selectedTab.listModel.items.length === 0) {
                // If the selected list model is now empty focus the selected tab list header
                elementToFocus = this.documentService.nativeDocument.getElementById(`tab-list-header-link-${selectedTab.id}`);
            } else if (eventElement && selectedTab !== this.pinnedTab) {
                // If pin state was changed on recent focus the same element
                elementToFocus = eventElement.nativeElement;
            } else {
                // If removing an item select the next available item
                const focusIndex = (selectedTab.listModel.maxItemCount ? index < selectedTab.listModel.maxItemCount : true) && index < selectedTab.listModel.items.length ? index : selectedTab.listModel.items.length;
                const item = selectedTab.listModel.items[focusIndex];
                elementToFocus = item ? this.documentService.nativeDocument.getElementById(`${selectedTab.id}-${item.getId()}`) : null;
            }
            if (elementToFocus) {
                elementToFocus.focus();
            }
        }, 0);
    }
    // In the RecentNewButton flight, we want to show the new and upload buttons when
    // the flight is enabled, we are in the Recent tab, it is empty, there are no errors and when
    // either the create or upload button is enabled.

    private isEmptyRecentView(): boolean {
        return this.recentTab &&
            this.recentTabListModel.tabs[this.selectedTab] === this.recentTab &&
            this.recentTab.listModel &&
            this.recentTab.listModel.items &&
            this.recentTab.listModel.items.length === 0 &&
            !this.recentTab.hasError &&
            !this.fileType;
    }

    private getSharedDocuments(): OperationStream<SharedServiceData> {
        return this.sharedWithMeService.getSharedDocuments(this.mruConfig.mruNumItemsToRequest, this.appFilter);
    }

    private getTamyDocuments(): OperationStream<MruData> {
        return this.officeFeedService.getTamyDocuments(this.maxTotalDocuments, this.appFilter);
    }

    private waitOnItemInflations(items: Array<MruFileItem>): Promise<Array<boolean>> {
        const joinedInflations = forkJoin(items.map(item => item.initialInflationCompleted));
        return joinedInflations.toPromise();
    }

    private processShared(data: Array<SharedServiceDataItem> | Array<Form>): ExpandableListModel {
        if (!data) {
            return;
        }
        let sharedItems = null;
        let maxItemCount = this.maxTotalDocuments;
        if (this.isFormsApp) {
            sharedItems = (data as Array<Form>).map((rawItem, index) => toFileItemFromForm(
                rawItem,
                `forms_shared_item_${index}`,
                'FormsSharedDocsImpression',
                this.contextService.isRtl,
                this.clientStartupConfigService.config.authType,
                false,
                MruFileFormat.SharedForm));
            maxItemCount = sharedItems.length;
        } else {
            sharedItems = (data as Array<SharedServiceDataItem>).map((rawItem, index) => toFileItemFromShared(
                rawItem,
                `shared_item_${index}`,
                'SharedDocsImpression',
                this.contextService.isRtl,
                this.clientStartupConfigService.config.authType,
                false));
        }
        return new ExpandableListModel(
            sharedItems,
            this.locService.ShowMoreMruResultsAriaLabel,
            this.locService.ShowLessMruResultsAriaLabel,
            this.locService.ShowMoreMruResultsUpdateAriaLabel,
            this.locService.ShowLessMruResultsUpdateAriaLabel,
            this.maxInitialDocuments,
            maxItemCount,
            (displayedItemCount, itemView) => this.logTabImpression(sharedItems, 'shared-mru', displayedItemCount, itemView, false),
            this.listAnimation,
            {
                isSmallMobileCards: this.isFormsApp,
                isEvenPadding: this.isFormsApp
            }
        );
    }

    // Todo: Remove extra function call
    private getNotebooks(): OperationStream<GraphGetOneNoteNotebooksResponse> {
        return this.onenoteGraphService.getNoteBooks(GetNotebookFetchStrategy.StaleWhileRevalidate);
    }

    private processAppItems(response: GraphGetOneNoteNotebooksResponse): Promise<ExpandableListModel> {
        return new Promise(resolve => {
            if (!response.value) {
                resolve(undefined);
                return;
            }
            const allPromises = [];
            for (const rawItem of response.value) {
                if (this.fileType === FileType.OneNote) {
                    allPromises.push(Promise.resolve(rawItem));
                }
            }

            Promise.all(allPromises).then(results => {
                const appItems = results.filter(rawItem => rawItem !== null).map((rawItem, index) => {
                    if (this.fileType === FileType.OneNote) {
                        return toFileItemFromNoteBook(
                            rawItem,
                            `${FileItem.fileTypeNameFromType(this.fileType)}_item_${index}`,
                            `${FileItem.fileTypeNameFromType(this.fileType)}Impression`,
                            this.contextService.isRtl,
                            this.clientStartupConfigService.config.authType,
                            false);
                    }
                });

                resolve(new ExpandableListModel(
                    appItems,
                    this.locService.ShowMoreMruResultsAriaLabel,
                    this.locService.ShowLessMruResultsAriaLabel,
                    this.locService.ShowMoreMruResultsUpdateAriaLabel,
                    this.locService.ShowLessMruResultsUpdateAriaLabel,
                    this.maxInitialDocuments,
                    this.maxTotalDocuments,
                    (displayedItemCount, itemView) => this.logTabImpression(appItems, 'app-items-mru', displayedItemCount, itemView, false),
                    this.listAnimation,
                    {
                        isSmallMobileCards: this.isFormsApp,
                        isEvenPadding: this.isFormsApp
                    }
                ));
            });
        });
    }

    private getClassNoteBooks(): Promise<ExpandableListModel> {
        return this.oneNoteService.getClassNoteBooks().then(data => this.processClassNoteBooks(data));
    }

    private processClassNoteBooks(data: Array<ClassNoteBook>): ExpandableListModel {
        if (!data) {
            return;
        }

        const classNoteBooks = data.map((rawItem, index) => toFileItemFromClassNoteBook(
            rawItem,
            `classnotebook_item_${index}`,
            'ClassNoteBookImpression',
            this.contextService.isRtl,
            this.clientStartupConfigService.config.authType,
            false));

        return new ExpandableListModel(
            classNoteBooks,
            this.locService.ShowMoreMruResultsAriaLabel,
            this.locService.ShowLessMruResultsAriaLabel,
            this.locService.ShowMoreMruResultsUpdateAriaLabel,
            this.locService.ShowLessMruResultsUpdateAriaLabel,
            this.maxInitialDocuments,
            this.maxTotalDocuments,
            (displayedItemCount, itemView) => this.logTabImpression(classNoteBooks, 'classNotebook-mru', displayedItemCount, itemView, false),
            this.listAnimation
        );
    }

    private createClassNoteBookLoadingTab(): TabItem {
        const classNoteBookTab = this.createClassNoteBookTab();

        classNoteBookTab.onLoad = () =>
            this.getClassNoteBooks().then(listModel => {
                classNoteBookTab.listModel = listModel;
            });
        return classNoteBookTab;
    }

    private createClassNoteBookTab(listModel?: ExpandableListModel): TabItem {
        const classNotebookTabTitle = this.locService.ClassNotebookTabTitle;
        const classNoteBookTab: TabItem = {
            id: 'classNotebook-mru',
            title: classNotebookTabTitle,
            ariaLabel: this.locService.format(this.locService.DocumentTabAriaLabelFormat, classNotebookTabTitle),
            listModel,
            zeroExperienceTitle: this.locService.NoClassNotebooksTitle,
            zeroExperienceSubtitle: this.locService.NoClassNotebooksSubTitle,
            zeroExperienceImage: /*TODO: replace with emptystate for class notebook tab*/ 'zero-docs-icon-sprite zero-docs-icon-sprite-icon-emptyfolder-208x208',
            errorTitle: this.locService.MruErrorTitle,
            errorSubtitle: this.locService.MruErrorSubtitle,
            errorImage: 'emptystate-icons-sprite emptystate-error',
            showZeroExperience: true,
            attribution: null,
            loaded: !!listModel,
            fromCache: false
        };
        return classNoteBookTab;
    }

    private createMyAppItemsLoadingTab(fileType: FileType.OneNote): TabItem {
        if (fileType !== FileType.OneNote) {
            return;
        }
        let reloadNetworkData = false;
        this.myAppItemsTab = this.createMyAppItemsTab();

        const processTabTask = response => new Promise<void>(resolve => {
            this.processAppItems(response).then(listModel => {
                if (listModel) {
                    this.myAppItemsTab.listModel = listModel;
                    this.myAppItemsTab.fromCache = response.fromCache;
                    this.myAppItemsTab.loaded = true;
                    if (!response.fromCache && reloadNetworkData) {
                        this.recentTabListModel.updateOrAddTab(this.myAppItemsTab);
                    }
                }
                resolve();
            });
        });

        const onLoadFunction = () => new Promise<void>((resolve, reject) => {
            let appItemStream: OperationStream<Cacheable> = null;

            if (fileType === FileType.OneNote) {
                appItemStream = this.getNotebooks();
            }

            this.appItemStreamSubscription = appItemStream.results.subscribe(data => {
                if (data.fromCache) {
                    reloadNetworkData = true;
                }
                processTabTask(data).then(() => { resolve(); });
            });
            appItemStream.initialCompletion.then(error => {
                reloadNetworkData = true;
                if (!error) {
                    return;
                }

                this.globalErrorHandler.handleCacheObservableError(error, ErrorId.uLfZP, ErrorId.yUFz9);
                if (error.cacheError && error.networkError) {
                    reject();
                }
            });
        });

        this.myAppItemsTab.onLoad = onLoadFunction;
        return this.myAppItemsTab;
    }

    private createMyAppItemsTab(listModel?: ExpandableListModel): TabItem {
        const myAppItemsTab: TabItem = {
            id: 'app-items-mru',
            title: '',
            ariaLabel: '',
            listModel,
            zeroExperienceTitle: '',
            zeroExperienceSubtitle: '',
            zeroExperienceImage: 'emptystate-icons-sprite emptystate-recent',
            errorTitle: this.locService.MruErrorTitle,
            errorSubtitle: this.locService.MruErrorSubtitle,
            errorImage: 'emptystate-icons-sprite emptystate-error',
            showZeroExperience: true,
            attribution: null,
            loaded: !!listModel,
            fromCache: false,
            showCardsInTileView: this.showInCardView
        };

        if (this.fileType === FileType.OneNote) {
            myAppItemsTab.zeroExperienceTitle = this.locService.NoOwnedOneNoteItems;
            myAppItemsTab.zeroExperienceSubtitle = this.locService.NoOwnedOneNoteItemsMemo;
            myAppItemsTab.title = this.locService.MyNotebooksTabTitle;
            myAppItemsTab.ariaLabel = this.locService.format(this.locService.DocumentTabAriaLabelFormat, this.locService.MyNotebooksTabTitle);
        }
        return myAppItemsTab;
    }

    private createSharedLoadingTab() {
        this.sharedWithMeTab = this.createSharedTab();
        this.sharedWithMeTab.onLoad = () => new Promise<void>((resolve, reject) => {
            let sharedDocStream = null;
            if (this.isFormsApp) {
                sharedDocStream = this.formsService.getSharedForms();
            } else {
                sharedDocStream = this.getSharedDocuments();
            }
            let reloadNetworkData = false;
            this.sharedWithMeSubscription = sharedDocStream.results.subscribe(data => {
                if (data.fromCache) {
                    reloadNetworkData = true;
                }
                this.processSharedDocTask(data, reloadNetworkData);
                resolve();
            });
            sharedDocStream.initialCompletion.then(error => {
                reloadNetworkData = true;
                if (!error) {
                    return;
                }
                this.globalErrorHandler.handleCacheObservableError(error, ErrorId.Vu9AW, ErrorId.idjiA);
                if (error.cacheError && error.networkError) {
                    reject();
                }
            });
        });
    }

    private processSharedDocTask(data: SharedServiceData | GetFormsResponse<Form>, reloadNetworkData: boolean) {
        if (!this.sharedData || !data.fromCache) {
            this.sharedData = data;
        }
        let listModel = null;
        if (this.isFormsApp) {
            listModel = this.processShared((this.sharedData as GetFormsResponse<Form>).value);
        } else {
            listModel = this.processShared((this.sharedData as SharedServiceData).items);
        }
        this.sharedWithMeTabListData = listModel.items;
        this.sharedWithMeTab.listModel = listModel;
        this.sharedWithMeTab.fromCache = this.sharedData.fromCache;
        this.sharedWithMeTab.loaded = true;
        if (!this.sharedData.fromCache && reloadNetworkData) {
            this.recentTabListModel.updateOrAddTab(this.sharedWithMeTab);
        }
    }

    private createSharedTab(): TabItem {
        const sharedTabTitle = this.locService.SharedDocumentsSectionTitleLowercase;

        const sharedTab: TabItem = {
            id: 'shared-mru',
            title: sharedTabTitle,
            ariaLabel: this.locService.format(this.locService.DocumentTabAriaLabelFormat, sharedTabTitle),
            listModel: null,
            zeroExperienceTitle: this.locService.appResource(AppSpecificResource.NoSharedWithMeTitle, this.fileType),
            zeroExperienceSubtitle: this.locService.appResource(AppSpecificResource.NoSharedWithMeSubtitle, this.fileType),
            zeroExperienceImage: this.newMruEmptyStateEnabled
                ? (this.darkMode ? 'empty-state-image empty-state-image--shared-dark' : 'empty-state-image empty-state-image--shared')
                : (this.darkMode ? 'emptystate-icons-sprite-darkmode emptystate-sharedwithme-darkmode' : 'emptystate-icons-sprite emptystate-swm'),
            errorTitle: this.locService.MruErrorTitle,
            errorSubtitle: this.locService.MruErrorSubtitle,
            errorImage: 'emptystate-icons-sprite emptystate-error',
            showZeroExperience: true,
            attribution: this.createAttribution(this.mruConfig.oneDriveSharedViewId),
            loaded: false,
            fromCache: false,
            showCardsInTileView: this.showInCardView
        };

        return sharedTab;
    }

    private processTamy(data: MruData): ExpandableListModel {
        const dataItems = data.items || [];
        const trendingItems = dataItems.map((item, index) => toFileItemFromOfficeFeed(
            item,
            `trending_item_${index}`,
            data.impressionId,
            this.contextService.isRtl,
            this.clientStartupConfigService.config.authType,
            data.fromCache,
            true));

        return new ExpandableListModel(
            trendingItems,
            this.locService.ShowMoreMruResultsAriaLabel,
            this.locService.ShowLessMruResultsAriaLabel,
            this.locService.ShowMoreMruResultsUpdateAriaLabel,
            this.locService.ShowLessMruResultsUpdateAriaLabel,
            this.maxInitialDocuments,
            this.maxTotalDocuments,
            (displayedItemCount, itemView) => this.logTabImpression(trendingItems, 'trending-docs', displayedItemCount, itemView, false),
            this.listAnimation,
            {
                initialTileItemCount: this.maxInitialDocumentCards
            }
        );
    }

    private createTrendingLoadingTab() {
        const trendingTabTitle = this.locService.TrendingDocumentsSectionTitle;
        this.trendingTab = {
            id: 'trending-docs',
            title: trendingTabTitle,
            ariaLabel: this.locService.format(this.locService.DocumentTabAriaLabelFormat, trendingTabTitle),
            listModel: null,
            zeroExperienceTitle: this.locService.appResource(AppSpecificResource.NoDiscoverTitle, this.fileType),
            zeroExperienceSubtitle: this.locService.appResource(AppSpecificResource.NoDiscoverSubtitle, this.fileType),
            zeroExperienceImage: this.darkMode ? 'emptystate-icons-sprite-darkmode emptystate-discover-darkmode' : 'emptystate-icons-sprite emptystate-discover',
            errorTitle: this.locService.MruErrorTitle,
            errorSubtitle: this.locService.MruErrorSubtitle,
            errorImage: 'emptystate-icons-sprite emptystate-error',
            showZeroExperience: true,
            attribution: this.createAttribution(this.mruConfig.oneDriveDiscoverViewId),
            loaded: false,
            fromCache: false,
            showCardsInTileView: true
        };

        this.trendingTab.onLoad = () =>
            new Promise<void>((resolve, reject) => {
                let reloadNetworkData = false;
                const trendingDocStream = this.getTamyDocuments();
                this.trendingDocSubscription = trendingDocStream.results.subscribe(data => {
                    if (data.fromCache) {
                        reloadNetworkData = true;
                    }
                    this.processTamyTask(data, reloadNetworkData);
                    resolve();
                });

                trendingDocStream.initialCompletion.then(error => {
                    reloadNetworkData = true;
                    if (!error) {
                        return;
                    }

                    this.globalErrorHandler.handleCacheObservableError(error, ErrorId.IyhlP, ErrorId.HFUrB);
                    if (error.cacheError && error.networkError) {
                        reject();
                    }
                });
            });
    }

    private processTamyTask(data: MruData, reloadNetworkData: boolean) {
        const listModel = this.processTamy(data);
        this.trendingTab.listModel = listModel;
        this.trendingTab.fromCache = data.fromCache;
        this.trendingTab.loaded = true;
        if (!data.fromCache && reloadNetworkData) {
            this.recentTabListModel.updateOrAddTab(this.trendingTab);
        }
    }

    private processRecentMruTask(data: MruData) {
        if (this.contextService.isNewConsumerUserExperience && (!data.items || !data.items.length)) {
            this.forceHideMru = true;
            return;
        }

        const mruItems = (data.items || []).map((rawItem, index) => toFileItemFromRecent(
            rawItem,
            `mru_item_${index}`,
            data.impressionId,
            this.locService,
            this.contextService.isRtl,
            this.clientStartupConfigService.config.authType,
            data.fromCache));

        // Not all document item data is available with response returned from MRU service.
        // Additional attribution data is fetched asynchronously using mru-item-inflation service.
        // If document items already exist (e.g. from cache) then there is a good chance that many
        // of them will still be present in new set. These items can be reused even if it is just
        // temporarily until "live" data is fetched.
        if (this.documents) {
            // Inflation data is only available for items that were rendered which is capped at this.maxTotalDocuments
            const existingMruItemsWithInflationData = this.documents.getAllUnpinnedDocuments().slice(0, this.maxTotalDocuments)
                .concat(this.documents.getAllPinnedDocuments().slice(0, this.maxTotalDocuments));
            this.copyInflationData(existingMruItemsWithInflationData, mruItems);
        }

        this.documents = new DocumentCollection(mruItems);
        this.impressionId = data.impressionId;

        let pinnedItemListModel: ExpandableListModel;
        let recentItemListModel: ExpandableListModel;

        if (this.pinnedDocsEnabled) {
            const pinnedDocs = this.documents.getAllPinnedDocuments();
            pinnedItemListModel = new ExpandableListModel(
                pinnedDocs,
                this.locService.ShowMoreMruResultsAriaLabel,
                this.locService.ShowLessMruResultsAriaLabel,
                this.locService.ShowMoreMruResultsUpdateAriaLabel,
                this.locService.ShowLessMruResultsUpdateAriaLabel,
                this.maxInitialDocuments,
                pinnedDocs.length,
                (displayedItemCount, itemView) => this.logTabImpression(
                    this.pinnedTab.listModel.items as Array<MruFileItem>,
                    'pinned-mru',
                    displayedItemCount,
                    itemView,
                    data.fromCache
                ),
                this.listAnimation,
                {
                    isSmallMobileCards: this.isFormsApp,
                    isEvenPadding: this.isFormsApp,
                    isVisualRefresh: this.isVisualRefresh
                }
            );

            recentItemListModel = new ExpandableListModel(
                this.documents.getAllDocuments(),
                this.locService.ShowMoreMruResultsAriaLabel,
                this.locService.ShowLessMruResultsAriaLabel,
                this.locService.ShowMoreMruResultsUpdateAriaLabel,
                this.locService.ShowLessMruResultsUpdateAriaLabel,
                this.maxInitialDocuments,
                this.maxTotalDocuments,
                (displayedItemCount, itemView) => {
                    this.logTabImpression(
                        this.recentTab.listModel.items as Array<MruFileItem>,
                        'pinned-mru',
                        displayedItemCount,
                        itemView,
                        data.fromCache);
                    this.focusFirstItem = false;
                },
                this.listAnimation,
                {
                    infiniteShowMoreEnabled: this.mruConfig.infiniteShowMoreEnabled,
                    isSmallMobileCards: this.isFormsApp,
                    isEvenPadding: this.isFormsApp,
                    isVisualRefresh: this.isVisualRefresh
                }
            );
        } else {
            recentItemListModel = new ExpandableListModel(
                mruItems,
                this.locService.ShowMoreMruResultsAriaLabel,
                this.locService.ShowLessMruResultsAriaLabel,
                this.locService.ShowMoreMruResultsUpdateAriaLabel,
                this.locService.ShowLessMruResultsUpdateAriaLabel,
                this.maxInitialDocuments,
                this.maxTotalDocuments,
                (displayedItemCount, itemView) => {
                    this.logTabImpression(
                        this.recentTab.listModel.items as Array<MruFileItem>,
                        'mru',
                        displayedItemCount,
                        itemView,
                        data.fromCache);
                    this.focusFirstItem = false;
                },
                this.listAnimation,
                {
                    infiniteShowMoreEnabled: this.mruConfig.infiniteShowMoreEnabled,
                    isSmallMobileCards: this.isFormsApp,
                    isEvenPadding: this.isFormsApp,
                    isVisualRefresh: this.isVisualRefresh
                }
            );
        }

        // This ensures the MRU recent and pinned tabs appear first without having to wait on shared or trending data
        const unpinnedTabTitle = this.locService.RecentDocumentsSectionTitleLowercase;
        this.recentTab = {
            id: this.pinnedDocsEnabled ? 'unpinned-mru' : 'mru',
            title: unpinnedTabTitle,
            ariaLabel: this.locService.format(this.locService.DocumentTabAriaLabelFormat, unpinnedTabTitle),
            listModel: recentItemListModel,
            timestampHeader: this.lastOpened,
            zeroExperienceTitle: this.newMruEmptyStateEnabled ? this.locService.NoRecentNewConsumerTitle : this.locService.appResource(AppSpecificResource.NoRecentTitle, this.fileType),
            zeroExperienceSubtitle: this.newMruEmptyStateEnabled ? (this.uploadEnabled ? this.locService.NoRecentNewConsumerSubtitle : this.locService.NoRecentNewConsumerSubtitleWithNoUpload) : this.uploadConfigService.config && this.uploadConfigService.config.dragAndDropEnabled && this.fileType !== FileType.Sway ?
                        this.locService.appResource(AppSpecificResource.NoRecentSubtitleDragAndDrop, this.fileType) :
                        this.locService.appResource(AppSpecificResource.NoRecentSubtitle, this.fileType),
            zeroExperienceImage: this.newMruEmptyStateEnabled
                ? (this.darkMode ? 'empty-state-image empty-state-image--recent-dark' : 'empty-state-image empty-state-image--recent')
                : (this.darkMode ? 'emptystate-icons-sprite-darkmode emptystate-recent-darkmode' : 'emptystate-icons-sprite emptystate-recent'),
            showZeroExperience: true,
            errorTitle: null,
            attribution: this.createAttribution(this.mruConfig.oneDriveRecentViewId),
            loaded: true,
            fromCache: data.fromCache,
            footerText: null,
            showCardsInTileView: this.showInCardView
        };
        this.recentTabListModel.updateOrAddTab(this.recentTab);

        if (pinnedItemListModel) {
            let tabTitle;
            let tabZeroExperienceTitle;
            let tabZeroExperienceSubtitle;
            if (this.capabilitiesService.capabilities.replacePinActionEnabled) {
                // Favorite strings
                tabTitle = this.locService.FavoritedDocumentSectionTitle;
                tabZeroExperienceTitle = this.locService.NoFavoritedTitle;
                tabZeroExperienceSubtitle = this.locService.NoFavoritedSubtitle;
            } else {
                // Default strings
                tabTitle = this.locService.PinnedDocumentsSectionTitleLowercase;
                tabZeroExperienceTitle = this.locService.appResource(AppSpecificResource.NoPinnedTitle, this.fileType);
                tabZeroExperienceSubtitle = this.locService.appResource(AppSpecificResource.NoPinnedSubtitle, this.fileType);
            }

            this.pinnedTab = {
                id: 'pinned-mru',
                title: tabTitle,
                ariaLabel: this.locService.format(this.locService.DocumentTabAriaLabelFormat, tabTitle),
                listModel: pinnedItemListModel,
                timestampHeader: this.lastOpened,
                zeroExperienceTitle: tabZeroExperienceTitle,
                zeroExperienceSubtitle: tabZeroExperienceSubtitle,
                zeroExperienceImage: this.newMruEmptyStateEnabled
                    ? (this.darkMode ? 'empty-state-image empty-state-image--pinned-dark' : 'empty-state-image empty-state-image--pinned')
                    : (this.darkMode ? 'emptystate-icons-sprite-darkmode emptystate-pinned-darkmode' : 'emptystate-icons-sprite emptystate-pinned'),
                showZeroExperience: true,
                errorTitle: null,
                attribution: this.createAttribution(this.mruConfig.oneDriveRecentViewId),
                loaded: true,
                fromCache: data.fromCache,
                showCardsInTileView: this.showInCardView
            };
            this.recentTabListModel.updateOrAddTab(this.pinnedTab);
        }

        this.oneDriveUploadService.uploadEnabledPromise.then(uploadEnabled => {
            if (uploadEnabled && this.uploadConfigService.config && this.uploadConfigService.config.dragAndDropEnabled && this.fileType !== FileType.Sway && this.fileType !== FileType.OneNote && !this.isFormsApp) {
                this.recentTab.footerText = this.locService.UploadFooter;
            }
        });

        this.updateNumLoggableItems();

        if (recentItemListModel.items.length) {
            this.markMruReady(data.fromCache);
            this.updateWithNewZeroStateCheck();
        } else {
            const promises: Array<Promise<void | boolean>> = [this.oneDriveUploadService.uploadEnabledPromise, this.initialTabsPromise, this.createMenuService.createButtonEnabledPromise];

            Promise.all(promises).then(() => {
                this.markMruReady(data.fromCache);
                this.updateWithNewZeroStateCheck();
            });
        }

        // Emitting Displayed irrespective of whether there are any items in recentItemListModel
        this.stateChanged.emit({ state: SectionLoadState.Displayed });

        if (!MruComponent.shellDeeplinkPromise) {
            MruComponent.shellDeeplinkPromise = this.activatedRoute.queryParams.pipe(take(1)).toPromise().then(params => {
                const section = params['section'] as string;
                if (section && section.toLowerCase() === 'recentdocs') {
                    this.initialListState = ListState.Open;
                    this.focusFirstItem = true;
                    // Wait 1 second for animations to complete before scrolling to the recent tab
                    setTimeout(() => this.recentTabElement.nativeElement.scrollIntoView(true), 1000);
                }
            });
        }

        if (this.showAnalytics) {
            const prefetchItems = [
                ...this.documents.getAllPinnedDocuments().slice(0, this.maxTotalDocuments),
                ...this.documents.getAllUnpinnedDocuments().slice(0, this.maxTotalDocuments)
            ];
            this.analyticsService.prefetchAnalytics(prefetchItems, data.fromCache);
        }

        if (!this.appFilter || this.appFilter.size === 0) {
            // Add recent docs to the PWA jump list
            this.jumpListService.registerRecentJumpList(mruItems);
        }
    }

    private copyInflationData(fromItems: Array<MruFileItem>, toItems: Array<MruFileItem>) {
        for (const toItem of toItems) {
            let matchingItem: MruFileItem = null;
            if (fromItems.some(fromItem => {
                if (fromItem.documentUrl === toItem.documentUrl) {
                    matchingItem = fromItem;
                    return true;
                } else {
                    return false;
                }
            })) {
                toItem.copyInflationData(matchingItem, this.mruConfig.mruActivityEnabled);
            }
        }
    }

    private createAttribution(view: string): TabItemAttribution {
        if (this.fileType === FileType.Sway) {
            return {
                title: this.locService.AllSways,
                url: this.urlAuthenticatorService.appendPvrAndUpnAuthParams(this.swayServiceConfigService.config.myEndpoint),
                tooltip: this.locService.AllSwaysLinkTooltip,
                ariaLabel: this.locService.AllSwaysLinkAriaLabel,
                referral: KnownWorkloadReferralNames.Sway,
                zeroTitle: this.locService.AllSways
            };
        } else if (this.isFormsApp) {
            return this.formsService.getAllMyFormsLink();
        }  else {
            const addLoginHint = this.capabilitiesService.capabilities.outgoingLoginHintEnabled;
            const loginHint = this.clientStartupConfigService.config.loginHint;
            const attribution = {
                title: this.locService.MoreInOneDrive,
                url: UrlBuilder.appendLoginHint(this.contextService.sharedContext.oneDriveUrl, loginHint, addLoginHint),
                ariaLabel: this.locService.MoreInOneDrive,
                referral: KnownWorkloadReferralNames.OneDrive,
                zeroTitle: this.locService.GoToOneDrive
            };

            this.oneDriveService.getMyDriveRootUrl()
                .then(rootUrls => {
                    if (rootUrls && rootUrls.userRoot) {
                        const params = {};
                        if (this.clientStartupConfigService.config.authType === AuthType.MSA) {
                            params['qt'] = view;
                        } else {
                            params['view'] = view;
                        }

                        if (addLoginHint) {
                            params['login_hint'] = loginHint;
                        }

                        attribution.url = UrlBuilder.appendQueryParams(rootUrls.userRoot, params);
                    }
                })
                .catch(error => {
                    this.globalErrorHandler.handleError(error, ErrorId.u9Mjx);
                });

            return attribution;
        }
    }

    // In the RecentNewButton flight, we want to show the new and upload buttons when
    // the flight is enabled, we are in the Recent tab, it is empty, there are no errors and when
    // either the create or upload button is enabled.

    private updateWithNewZeroStateCheck() {
        this.emptyRecentNewButtonEnabled = this.isEmptyRecentView();
        this.recentTab.attribution = (!this.mobile && this.emptyRecentNewButtonEnabled) ? null : this.createAttribution(this.mruConfig.oneDriveRecentViewId);
    }

    private updateListModels(filterText?: string) {
        const stringMatch = filterText && new RegExp(filterText, 'i');
        if (this.pinnedDocsEnabled) {
            this.pinnedTab.listModel.items = this.documents.getAllPinnedDocuments()
                .filter(item => !stringMatch || stringMatch.test(item.fileName));
            this.pinnedTab.listModel.maxItemCount = this.pinnedTab.listModel.items.length;
        }
        if (this.sharedWithMeTab && this.sharedWithMeTabListData) {
            this.sharedWithMeTab.listModel.items = this.sharedWithMeTabListData
                .filter(item => !stringMatch || stringMatch.test(item.fileName));
        }
        this.recentTab.listModel.items = this.documents.getAllDocuments()
            .filter(item => !stringMatch || stringMatch.test(item.fileName));
        this.updateNumLoggableItems();
        this.updateWithNewZeroStateCheck();
    }

    /**
     * Logs an impression for when the Show More/Show Less links are clicked. This is also used to log the
     * initial MRU impression for each individual MRU list (pinned, unpinned, shared with me, and discover)
     * Cases to consider when modifying this:
     *     Navigating to app gallery before inflation process has finished.
     *     Navigating to another tab in the tab list before inflation process has finished
     *     Logging correct impression when inflation process fails. (Right now the inflation process catches errors internally, so we don't catch errors here)
     * @param items All items in the list.
     * @param listId Id of the mru list that was rendered
     * @param numRenderedItems number of items that were actually rendered on screen.
     * @param state State of the tab content.
     * @param fromCache Indicates whether content comes from cache.
     */
    private logTabImpression(
        items: Array<MruFileItem>,
        listId: KnownTabIds,
        numRenderedItems: number,
        itemView: ItemView,
        fromCache: boolean,
        state: TabItemState = TabItemState.Loaded
    ) {
        const renderedItems = items.slice(0, numRenderedItems);

        // TODO: We don't need to wait to log OTEL impression since we don't include inflation data
        this.logOtelImpressionEvent(items, listId, renderedItems, fromCache, state);

        if (this.pendingTabImpressionEvent) {
            this.logPendingTabImpression();
        }

        const event = this.createImpressionEvent(items, listId, renderedItems, itemView, fromCache, state);

        const currentImpressionIndex = ++this.tabImpressionIndex;
        this.pendingTabImpressionEvent = {
            event,
            items: renderedItems,
            id: currentImpressionIndex
        };

        if (state !== TabItemState.Loaded) {
            this.logPendingTabImpression();
            return;
        } else {
            // When a tab finishes loading we need to update the loggable item count
            this.updateNumLoggableItems();
        }

        this.waitOnItemInflations(renderedItems).then(() => {
            if (this.pendingTabImpressionEvent && this.pendingTabImpressionEvent.id === currentImpressionIndex) {
                this.logPendingTabImpression();
            }
        });
    }

    private logPendingTabImpression() {
        if (!this.pendingTabImpressionEvent) {
            return;
        }

        const event = this.pendingTabImpressionEvent.event;
        this.fillInflatedImpressionParams(event.params, this.pendingTabImpressionEvent.items);
        event.record();
        this.pendingTabImpressionEvent = null;
    }

    private fillInflatedImpressionParams(params: InstrumentationParams, items: Array<MruFileItem>) {
        params[InstrumentationParam.ActivityDistribution] = items
            .map(i => this.activityDescriptionService.displayedActivityType(i.activities, i.lastViewedTime, true))
            .reduce((combined, current, index) => combined + (index === 0 ? '' : ';') + ActionType[current], '');
        params[InstrumentationParam.IsNewDistribution] = items
            .map(i => typeof i.isNew === 'boolean' ? i.isNew.toString() : 'undefined')
            .reduce((combined, current, index) => combined + (index === 0 ? '' : ';') + current, '');
        params[InstrumentationParam.IsSharedDistribution] = items
            .map(i => (i.sharing && typeof i.sharing.shared === 'boolean') ? i.sharing.shared.toString() : 'undefined')
            .reduce((combined, current, index) => combined + (index === 0 ? '' : ';') + current, '');
    }

    private createImpressionEvent(
        items: Array<MruFileItem>,
        listId: string,
        renderedItems: Array<MruFileItem>,
        itemView: ItemView,
        fromCache: boolean,
        state: TabItemState = TabItemState.Loaded,
        currentIndex?: number
    ): InstrumentationEvent {
        const loggableDisplayItems = !renderedItems || this.dataSecurityService.securityLevel >= DataSecurityLevel.Enhanced ? renderedItems : renderedItems.filter(i => !i.isLocal);
        const distribution = FileItem.distributionFromFileItems(loggableDisplayItems);
        const numRenderedItems = loggableDisplayItems ? loggableDisplayItems.length : 0;

        const params: InstrumentationParams = {};
        params[InstrumentationParam.RenderSize] = numRenderedItems.toString();
        params[InstrumentationParam.Distribution] = distribution;
        params[InstrumentationParam.ElementId] = listId;
        params[InstrumentationParam.TotalSize] = this.getNumLoggableItems(items).toString();
        params[InstrumentationParam.State] = itemView;
        params[InstrumentationParam.TabState] = TabItemState[state];
        params[InstrumentationParam.ImpressionId] = this.impressionId;
        params[InstrumentationParam.FromServiceWorkerCache] = (!!fromCache).toString();

        if (typeof currentIndex !== 'undefined') {
            params[InstrumentationParam.Index] = currentIndex.toString();
        }

        if (items.length > 0) {
            const timeSinceLastOpened = items[0].timeSinceLastOpenInSeconds();
            if (timeSinceLastOpened !== null) {
                params[InstrumentationParam.InactivityDuration] = timeSinceLastOpened.toString();
            }
        }

        return this.instrumentationService.generateEvent(KnownInstrumentationEvents.MruImpression, KnownInstrumentationEvents.GenericImpression, DataSecurityLevel.Security, params);
    }

    private logOtelImpressionEvent(items: Array<MruFileItem>, listId: KnownTabIds, renderedItems: Array<MruFileItem>, fromCache: boolean, state: TabItemState = TabItemState.Loaded) {
        const loggableDisplayItems = !renderedItems || this.dataSecurityService.securityLevel >= DataSecurityLevel.Enhanced ? renderedItems : renderedItems.filter(i => !i.isLocal);
        const distribution = FileItem.distributionFromFileItems(loggableDisplayItems);
        const numRenderedItems = loggableDisplayItems ? loggableDisplayItems.length : 0;
        logImpression({
            id: this.impressionId,
            eventName: state === TabItemState.Loaded ? KnownImpressionInstrumentationEvents.MruImpression : KnownImpressionInstrumentationEvents.MruImpression, // TODO: Add loading/error events
            area: 'Documents',
            subArea: tabIdToSubArea[listId],
            fromCache: !!fromCache,
            renderSize: numRenderedItems,
            totalSize: this.getNumLoggableItems(items),
            distribution
        });
    }

    private markMruReady(fromCache: boolean) {
        this.performanceService.mark(fromCache ? MruPerformanceMarker.CachedMruReady : MruPerformanceMarker.MruReady);
        this.browserPaintMeasurementService.recordWhenPainted(PaintMarkerName.MRU);
    }

    private updateNumLoggableItems() {
        const relevantTab = this.recentTabListModel.tabs[this.selectedTab];
        this.numLoggableItems = relevantTab && relevantTab.listModel ?
            this.getNumLoggableItems(relevantTab.listModel.items as Array<MruFileItem>)
            : 0;
    }

    private getNumLoggableItems(items: Array<MruFileItem>) {
        if (!items) {
            return 0;
        }
        return this.dataSecurityService.securityLevel >= DataSecurityLevel.Enhanced ? items.length : items.filter(i => !i.isLocal).length;
    }

    private sendMruItemState(mruItemState: MruItemStates, item: MruFileItem) {
        this.eventService.broadcastEvent({
            name: AngularEvents.mruItemStateChange,
            args: [
                mruItemState,
                item
            ]
        });
    }
}
